/**
 * Centralized progress management for import operations
 * 
 * Provides a simple, DRY interface for tracking import progress
 * with correct percentage calculations and consistent messaging.
 */

import type { NotificationType } from '../../components/Notification'

export type ImportProgress = {
  processed: number
  total: number
  stage: 'scanning' | 'creatingFolders' | 'importingNotes' | 'done'
}

export type ProgressManager = {
  begin: (total: number) => void
  step: (count?: number) => void
  done: (completionNotification: NotificationType) => void
}

/**
 * Creates a progress manager that handles all progress tracking logic
 * 
 * @param showNotification - Function to show initial notification
 * @param replaceNotification - Function to replace current notification
 * @returns ProgressManager instance
 */
export function createProgressManager(
  showNotification: (notification: NotificationType) => void,
  replaceNotification: (notification: NotificationType) => void
): ProgressManager {
  let progress: ImportProgress = {
    processed: 0,
    total: 0,
    stage: 'scanning'
  }

  const renderProgress = (currentProgress: ImportProgress): NotificationType => {
    const percentage = currentProgress.total === 0 
      ? 0 
      : Math.round((currentProgress.processed / currentProgress.total) * 100)
    
    return {
      title: 'Importing folder…',
      message: `${String(percentage)}% completed`,
      autoHide: false,
    }
  }

  return {
    begin: (total: number) => {
      progress = { 
        processed: 0, 
        total, 
        stage: 'importingNotes' 
      }
      
      // Skip progress UI if nothing to do
      if (total === 0) {
        return
      }

      const initialNotification = renderProgress(progress)
      showNotification(initialNotification)
    },

    step: (count = 1) => {
      progress.processed += count

      // Only update if we have a meaningful total
      if (progress.total > 0) {
        const notification = renderProgress(progress)
        replaceNotification(notification)
      }
    },

    done: (completionNotification: NotificationType) => {
      progress.stage = 'done'
      replaceNotification(completionNotification)
    }
  }
}
